// Forced-movement helpers. Pure. No React, no Three.js.
//
// ── Codex (Forced Movement) ──
//
// Sorcery cards routinely move OTHER units to new cells outside the
// normal "spend a step to move yourself" flow:
//   • TELEPORT. Pick a unit, pick a destination cell from a defined
//                scope (nearby of unit, caster's cell, etc.). Source
//                cell of the unit is unconstrained.
//   • PULL. Move a unit to the source's cell (one pick).
//   • PUSH. Move a unit one cell directionally away from the
//                source (one pick, destination computed).
//
// This module owns the destination-set computation. The runtime
// (GameBoard) handles the actual mesh-position write + broadcast.
//
// ── Destination scope shapes ──
//
//   { scope: 'nearby_of_unit' }. 8-neighbor + self of the picked unit
//   { scope: 'adjacent_of_unit' }. 4-cardinal of the picked unit
//   { scope: 'nearby_of_source' }. 8-neighbor + self of the source/caster
//   { scope: 'adjacent_of_source' }. 4-cardinal of the source/caster
//   { scope: 'global' }. Any cell on the grid
//   { scope: 'source_cell' }. Exactly the source's cell (PULL)
//
// Some scopes reference the source (caster) cell which the caller
// provides via `opts.sourceCell`. Others reference the picked unit's
// own current cell via `opts.unitCell`. Helpers return a Set of
// "col,row" string keys for cheap lookup.

import { cellsInScope } from './scope';
import { hasKeyword } from '../core/keywordQuery';

// Codex: "moved by force." An effect moving a unit outside its
// controller's own move action. Units with the `unmovable` status are
// never legal subjects of forced movement (teleport / pull / push /
// mass-teleport); voluntary moves and self-moves are unaffected.
// Talamh Dreig: "Can't be moved by force."
export function isForceMovable(card) {
  return !hasKeyword(card, 'unmovable');
}

// Ops whose target pick moves the PICKED unit by force. Target-pick
// pipelines exclude unmovable cards for exactly these ops.
export const FORCED_MOVE_OPS = new Set([
  'teleport_target',
  'pull_to_source',
  'push_target_away',
  'swoop_and_burrow_target',
]);

/**
 * Compute every legal destination cell for a forced-move op.
 *
 * @param destinationScope { scope: <string> } as described above
 * @param opts {
 *   unitCell:   { col, row }. The picked unit's current cell
 *   sourceCell: { col, row }. The source/caster's cell (may be null
 *                              if not used by the scope)
 *   totalCols:  number. Grid width
 *   totalRows:  number. Grid height
 * }
 * @returns Set<string> of "col,row" keys.
 */
export function eligibleDestinations(destinationScope, opts = {}) {
  const scope = destinationScope?.scope;
  const { unitCell, sourceCell, totalCols, totalRows } = opts;
  if (!scope || typeof totalCols !== 'number' || typeof totalRows !== 'number') {
    return new Set();
  }

  if (scope === 'global') {
    const all = new Set();
    for (let c = 0; c < totalCols; c++) {
      for (let r = 0; r < totalRows; r++) {
        all.add(`${c},${r}`);
      }
    }
    return all;
  }

  if (scope === 'source_cell') {
    if (!sourceCell) return new Set();
    return new Set([`${sourceCell.col},${sourceCell.row}`]);
  }

  const anchor = scope.endsWith('_of_unit') ? unitCell
    : scope.endsWith('_of_source') ? sourceCell
    : null;
  if (!anchor) return new Set();

  if (scope.startsWith('nearby')) {
    return cellsInScope(anchor, 'nearby', totalCols, totalRows);
  }
  if (scope.startsWith('adjacent')) {
    return cellsInScope(anchor, 'adjacent', totalCols, totalRows);
  }
  return new Set();
}

/**
 * Compute the unique forced-push destination for a unit at `unitCell`
 * being pushed away from `sourceCell` by one step. Returns the new
 * cell, or null if the push would leave the grid OR is ambiguous
 * (caller can prompt or fizzle).
 *
 * Direction logic: if the unit and source share a row, push one step
 * along the column axis away from the source. If they share a column,
 * push along the row axis. If they're diagonal, the codex says the
 * controller picks the direction. We return null to signal ambiguity.
 *
 * Per codex, units pushed off the edge of the realm are sent to the
 * void; that policy is the caller's call (this returns null + the
 * runtime decides).
 */
export function pushDestination(unitCell, sourceCell, totalCols, totalRows) {
  if (!unitCell || !sourceCell) return null;
  const dc = unitCell.col - sourceCell.col;
  const dr = unitCell.row - sourceCell.row;
  if (dc === 0 && dr === 0) return null;        // pushed-from-same-cell undefined
  if (dc !== 0 && dr !== 0) return null;        // diagonal. Ambiguous
  const step = (n) => n > 0 ? 1 : (n < 0 ? -1 : 0);
  const next = { col: unitCell.col + step(dc), row: unitCell.row + step(dr) };
  if (next.col < 0 || next.col >= totalCols) return null;
  if (next.row < 0 || next.row >= totalRows) return null;
  return next;
}
